import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Button,
    TextField,
    Typography,
    IconButton,
    CircularProgress,
    Dialog
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import apilink from "../globals/Baseurls";
import { useNavigate } from "react-router-dom";
import SwipeableViews from "react-swipeable-views";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";

export default function UserProfile() {
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [navto, setNavto] = useState("matches");

    const [photoIndex, setPhotoIndex] = useState(0);
    const [fullScreenOpen, setFullScreenOpen] = useState(false);

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const chatData = JSON.parse(localStorage.getItem("chatSessionData"));

                const userId = chatData.user_id;
                const navTo = chatData.navto;
                setNavto(navTo);

                const { token } = JSON.parse(localStorage.getItem("userSessionData"));

                const res = await axios.get(
                    `${apilink.link}/user-profile/${userId}`,
                    {}, // empty body
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (res.data.success) {
                    const user = res.data.user;

                    // Sort photos: primary first
                    if (user.photos && user.photos.length > 0) {
                        user.photos.sort((a, b) => b.is_primary - a.is_primary);
                    }

                    setUserProfile(user);
                } else {
                    setError("Failed to fetch user profile");
                }
            } catch (err) {
                setError(err.message || "Something went wrong");
            } finally {
                setLoading(false);
            }
        };

        fetchUserProfile();
    }, []);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Box p={2} maxWidth={500} mx="auto">
            {/* Top row */}
            <Box display="flex" alignItems="center" mb={2}>
                <IconButton
                    onClick={() => navigate(`/${navto}`)}
                    sx={{
                        bgcolor: "#f0f0f0",
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": { bgcolor: "#e0e0e0" },
                    }}
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography variant="h6" ml={1}>User Profile</Typography>
            </Box>

            {/* Profile Card */}
            <Card sx={{ borderRadius: 3, overflow: "hidden", mt: 1, maxWidth: 420, mx: "auto" }}>
                {/* Swipeable Photos */}
                <SwipeableViews
                    index={photoIndex}
                    onChangeIndex={setPhotoIndex}
                    enableMouseEvents
                >
                    {userProfile.photos.map((photo, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                height: { xs: 260, sm: 320, md: 360 },
                                backgroundImage: `url(${apilink.base_link}/storage/${photo.path})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                position: "relative",
                            }}
                        >
                            {/* Fullscreen button */}
                            <IconButton
                                onClick={() => setFullScreenOpen(true)}
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    bgcolor: "rgba(0,0,0,0.4)",
                                    color: "#fff",
                                    borderRadius: "50%",
                                    p: 0.8,
                                    "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                                }}
                            >
                                <OpenInFullIcon />
                            </IconButton>

                            {/* Swipe hint */}
                            {userProfile.photos.length > 1 && (
                                <Typography
                                    sx={{
                                        position: "absolute",
                                        bottom: 8,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        bgcolor: "rgba(0,0,0,0.4)",
                                        color: "#fff",
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 1,
                                        fontSize: 12,
                                    }}
                                >
                                    Swipe photos
                                </Typography>
                            )}

                            {/* Photo counter */}
                            {userProfile.photos.length > 1 && (
                                <Typography
                                    sx={{
                                        position: "absolute",
                                        top: 8,
                                        left: 8,
                                        bgcolor: "rgba(0,0,0,0.5)",
                                        color: "#fff",
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        fontSize: 12,
                                        fontWeight: "bold",
                                    }}
                                >
                                    {photoIndex + 1} / {userProfile.photos.length}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </SwipeableViews>

                {/* Fullscreen Modal */}
                <Dialog
                    open={fullScreenOpen}
                    onClose={() => setFullScreenOpen(false)}
                    fullScreen
                    PaperProps={{
                        sx: {
                            bgcolor: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                        },
                    }}
                >
                    <IconButton
                        onClick={() => setFullScreenOpen(false)}
                        sx={{
                            position: "absolute",
                            top: 16,
                            left: 16,
                            bgcolor: "#e0e0e0",
                            color: "#000",
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            "&:hover": { bgcolor: "#d0d0d0" },
                            zIndex: 10,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <SwipeableViews
                        index={photoIndex}
                        onChangeIndex={setPhotoIndex}
                        enableMouseEvents
                        style={{ width: "100%", maxWidth: 420 }}
                    >
                        {userProfile.photos.map((photo, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "100vh",
                                }}
                            >
                                <img
                                    src={`${apilink.base_link}/storage/${photo.path}`}
                                    alt={`photo-${idx}`}
                                    style={{
                                        maxHeight: "100%",
                                        maxWidth: "100%",
                                        objectFit: "contain",
                                        borderRadius: 8,
                                    }}
                                />
                            </Box>
                        ))}
                    </SwipeableViews>
                </Dialog>

                {/* Profile Info */}
                <CardContent>
                    <Typography variant="h5">
                        {userProfile.profile.display_name}, {userProfile.profile.age}<Typography color="#0288d1" sx={{ fontSize: "12px" }}>
                            {userProfile.profile.gender.charAt(0).toUpperCase() + userProfile.profile.gender.slice(1)}

                        </Typography>
                    </Typography>
                    <Typography mt={1}>{userProfile.profile.bio}</Typography>
                    <Typography color="#0288d1" mt={1} sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: "12px" }}>
                        <SearchIcon color="info" fontSize="12px" /> {userProfile.profile.relation_type}
                    </Typography>
                </CardContent>
            </Card>
        </Box>
    );
};

